import { t, useStoreLanguage, getLocale, localizeDigits } from "../../../features/store-language/i18n.ts";
import LocalizedGridStatus, { LocalizedGroupValue } from "./LocalizedGridStatus.tsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import Localization from "./ag-langs";
import { AG_GRID_LOCALE_EG } from "@ag-grid-community/locale";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "./AgGrid.css";
import { Tooltip } from "antd";
import {
  CloseCircleFilled,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import LoadingRing from "../LoadingRing.jsx";
import GridRowActions, { GridInlineActions, isOperationColumn } from "./GridRowActions.tsx";
import useGridRowActions from "./useGridRowActions.js";
import useGridPreference from "./preferences/useGridPreference";
import { initialColumnDefaults } from "./preferences/gridPreferenceState";
import { gridPreferenceRegistry } from "./preferences/gridPreferenceRegistry";
import { PlatformIconButton } from "../../../features/platform-admin/PlatformAdminIcon.tsx";
import useMobileGrid from "./useMobileGrid.ts";

const FULLSCREEN_RESTORE_PREFIX = "ag-grid-fullscreen-restore-";

function localizeColumn(column) {
  const formatter = column.valueFormatter;
  return {
    ...column,
    children: column.children?.map(localizeColumn),
    getQuickFilterText: function (params) { return normalizeSearchValue(column.getQuickFilterText ? column.getQuickFilterText(params) : params.value); },
    filterParams: { textFormatter: normalizeSearchValue, ...column.filterParams },
    // Keep exported identifiers and numeric values in their original representation.
    useValueFormatterForExport: column.useValueFormatterForExport ?? (formatter ? undefined : false),
    valueFormatter: typeof formatter === "string" ? formatter : function (params) {
      const value = formatter ? formatter(params) : params.value;
      if (typeof value !== "number" && typeof value !== "string") return value == null ? "" : String(value);
      if (typeof value === "string" && /@|:\/\//.test(value)) return value;
      return localizeDigits(value);
    },
  };
}

function localizeStatusBar(statusBar) {
  const kinds = {
    agTotalAndFilteredRowCountComponent: "totalFiltered",
    agTotalRowCountComponent: "total",
    agFilteredRowCountComponent: "filtered",
    agSelectedRowCountComponent: "selected",
    agAggregationComponent: "aggregation",
  };
  if (!statusBar) return statusBar;
  return { ...statusBar, statusPanels: statusBar.statusPanels?.map(function (panel) {
    const kind = kinds[panel.statusPanel] || (panel.statusPanel === LocalizedGridStatus ? panel.statusPanelParams?.kind : undefined);
    return kind ? { ...panel, key: panel.key || `localized-${kind}`, statusPanel: LocalizedGridStatus, statusPanelParams: { ...panel.statusPanelParams, kind } } : panel;
  }) };
}


const readFullscreenRestore = (gridId) => {
  if (!gridId) {
    return false;
  }

  try {
    const key = `${FULLSCREEN_RESTORE_PREFIX}${gridId}`;
    const shouldRestore = sessionStorage.getItem(key) === "true";
    if (shouldRestore) {
      sessionStorage.removeItem(key);
    }
    return shouldRestore;
  } catch {
    return false;
  }
};

const writeFullscreenRestore = (gridId) => {
  if (!gridId) {
    return;
  }

  try {
    sessionStorage.setItem(`${FULLSCREEN_RESTORE_PREFIX}${gridId}`, "true");
  } catch {
    // ignore storage failures
  }
};

const hasExplicitLoading = (loading) =>
  loading !== undefined && loading !== null;

const normalizeSearchValue = (value) => {
  return String(value ?? "")
    .toLocaleLowerCase(getLocale())
    .replace(/[۰-۹]/g, (digit) => {
      return String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit));
    })
    .replace(/[٠-٩]/g, (digit) => {
      return String("٠١٢٣٤٥٦٧٨٩".indexOf(digit));
    })
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/\s+/g, " ")
    .trim();
};

const getColumnStateApi = (params) => {
  return params?.api || params?.columnApi;
};

const getContextMenuIcon = (icon) => {
  if (icon == null || icon === false) {
    return undefined;
  }

  return renderToStaticMarkup(<>{icon}</>);
};

const persistColumnState = (gridId, params) => {
  if (!gridId) {
    return;
  }

  const columnState = getColumnStateApi(params)?.getColumnState?.();
  if (!columnState) {
    return;
  }

  localStorage.setItem(`${gridId}`, JSON.stringify(columnState));
};

const AgGrid = (props) => {
  const storeLanguage = useStoreLanguage();
  const isMobileGrid = useMobileGrid();
  const gridApiRef = useRef();
  const prevDataRef = useRef(props.data);
  const isInitialMountRef = useRef(true);
  const awaitingAutoLoadRef = useRef(
    !props.hideOverlay && !hasExplicitLoading(props.loading),
  );
  const propsRef = useRef(props);
  propsRef.current = { ...props, isMobileGrid };
  const [filteredData, setFilteredData] = useState(props.data);
  const [dataSource, setDataSource] = useState(props.data);
  const [isFullscreen, setIsFullscreen] = useState(() =>
    readFullscreenRestore(props.id),
  );
  const [isLoading, setIsLoading] = useState(
    () => !props.hideOverlay && props.loading !== false,
  );
  const [searchText, setSearchText] = useState("");
  const [grouping, setGrouping] = useState(false);
  const groupingOpen = props.groupingPanelOpen ?? grouping;
  const rowActions = useGridRowActions(props, gridApiRef, isLoading);
  const inlineOperations = isMobileGrid && props.mobileRowActions;
  const rowActionsRef = useRef(rowActions);
  rowActionsRef.current = rowActions;
  const inlineRenderer = useCallback(function (params) {
    const current = rowActionsRef.current;
    return (
      <GridInlineActions
        actions={current.resolve(params.node)}
        disabled={propsRef.current.actionsDisabled || isLoading}
        pending={current.pending}
        onRun={function (action, event) { return rowActionsRef.current.run(action, event, params.node); }}
      />
    );
  }, [isLoading]);
  useEffect(function () {
    if (inlineOperations) gridApiRef.current?.api?.refreshCells({ force: true, columns: ["operation"] });
  }, [inlineOperations, rowActions.pending, props.actionsDisabled]);

  // Only the opted-in mobile editor renders command declarations as row controls.
  const normalizedColumns = useMemo(() => props.columns?.filter((column) => inlineOperations || !isOperationColumn(column)).map(function ({ mobileEssential, ...column }) {
    if (isOperationColumn(column)) return {
      ...column,
      colId: column.colId || column.field,
      flex: 0, width: 48, minWidth: 48, maxWidth: 48,
      resizable: false, sortable: false, filter: false, enableRowGroup: false,
      suppressColumnsToolPanel: true, suppressMovable: true,
      cellClass: "ag-grid-inline-actions-cell",
      headerClass: "ag-grid-inline-actions-header",
      cellRenderer: inlineRenderer,
    };
    return localizeColumn(column);
  }).map((column) =>
    gridPreferenceRegistry[props.id]
      ? initialColumnDefaults(isMobileGrid ? { ...column, floatingFilter: false, pinned: null } : column)
      : isMobileGrid ? { ...column, floatingFilter: false, pinned: null } : column,
  ), [props.columns, props.id, isMobileGrid, inlineOperations, inlineRenderer]);
  const preferenceColumns = useMemo(function () {
    return normalizedColumns?.filter(function (column) { return !isOperationColumn(column); });
  }, [normalizedColumns]);
  const inlineExportOptions = useMemo(function () {
    if (!inlineOperations) return {};
    const columnKeys = props.columns.filter(function (column) {
      return !isOperationColumn(column);
    }).map(function (column) { return column.colId || column.field; });
    return {
      defaultCsvExportParams: { ...props.gridOptions?.defaultCsvExportParams, columnKeys },
      defaultExcelExportParams: { ...props.gridOptions?.defaultExcelExportParams, columnKeys },
    };
  }, [inlineOperations, props.columns, props.gridOptions?.defaultCsvExportParams, props.gridOptions?.defaultExcelExportParams]);
  const preference = useGridPreference(props.id, preferenceColumns, isMobileGrid, props.gridPreferenceIdentity);
  const preferenceRef = useRef(preference);
  preferenceRef.current = preference;

  // Compact presentation never writes desktop preferences or removes columns from export.
  const compactState = useMemo(function () {
    if (!isMobileGrid || !props.compactColumns) return preference.initialState;
    const state = { ...preference.initialState };
    delete state.columnSizing;
    delete state.columnOrder;
    state.columnVisibility = { hiddenColIds: [] };
    return state;
  }, [isMobileGrid, props.compactColumns, preference.initialState]);
  const applyCompactColumns = useCallback(function (api) {
    if (!isMobileGrid || !props.compactColumns || !api || api.isDestroyed()) return;
    const state = props.columns.filter(function (column) { return inlineOperations || !isOperationColumn(column); }).map(function (column) {
      return {
        colId: column.colId || column.field,
        hide: Boolean(column.hide) || (!props.showAllMobileColumns && !column.mobileEssential),
      };
    });
    api.applyColumnState({ state });
  }, [isMobileGrid, props.compactColumns, props.columns, props.showAllMobileColumns, inlineOperations]);
  useEffect(function () {
    applyCompactColumns(gridApiRef.current?.api);
  }, [applyCompactColumns]);

  const getContextMenuItems = (params) => {
    const existing = props.gridOptions?.getContextMenuItems || props.getContextMenuItems;
    const defaults = (existing ? existing(params) : params.defaultItems) || [];
    const operations = rowActions.resolve(params.node).map((item) => ({
      name: item.label,
      tooltip: typeof item.tooltip === "string" ? item.tooltip : undefined,
      icon: getContextMenuIcon(item.icon),
      disabled: item.disabled || isLoading || props.actionsDisabled || Boolean(rowActions.pending) || rowActions.selectedCount > 1,
      cssClasses: [item.danger ? "erp-grid-context-operation-danger" : "erp-grid-context-operation-action"],
      action: () => rowActions.run(item, params.event, params.node),
    }));
    return operations.length ? [...operations, "separator", ...defaults] : defaults;
  };

  useEffect(() => {
    if (props.hideOverlay) {
      awaitingAutoLoadRef.current = false;
      setIsLoading(false);
      setDataSource(props.data);
      setFilteredData(props.data);
      prevDataRef.current = props.data;
      isInitialMountRef.current = false;
      return;
    }

    if (hasExplicitLoading(props.loading)) {
      awaitingAutoLoadRef.current = false;
      setIsLoading(!!props.loading);
      setDataSource(props.data);
      setFilteredData(props.data);
      prevDataRef.current = props.data;
      isInitialMountRef.current = false;
      return;
    }

    // Auto mode (most list pages): keep overlay until the first data update
    // after mount, then hide it. Never flash the loader only after data arrives.
    setDataSource(props.data);
    setFilteredData(props.data);

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevDataRef.current = props.data;
      awaitingAutoLoadRef.current = true;
      setIsLoading(true);
      return;
    }

    if (prevDataRef.current !== props.data) {
      prevDataRef.current = props.data;
      awaitingAutoLoadRef.current = false;
      setIsLoading(false);
    }
  }, [storeLanguage, props.data, props.loading, props.hideOverlay]);

  useEffect(() => {
    if (!isFullscreen) {
      return undefined;
    }

    document.body.classList.add("ag-grid-fullscreen-active");

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove("ag-grid-fullscreen-active");
      document.removeEventListener("keydown", handleEscape);
    };
  }, [storeLanguage, isFullscreen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 150);

    return () => clearTimeout(timer);
  }, [storeLanguage, isFullscreen]);

  LicenseManager.setLicenseKey(
    "DownloadDevTools_COM_NDEwMjM0NTgwMDAwMA==59158b5225400879a12a96634544f5b6",
  );

  const getSharedGridOptions = useCallback(() => ({
    // Grid Configuration
    maintainColumnOrder: true,
    paginationNumberFormatter: function (params) { return params.value.toLocaleString(getLocale()); },
    autoGroupColumnDef: { cellRendererParams: { suppressCount: true, innerRenderer: LocalizedGroupValue } },
    localeText: storeLanguage === "fa" ? Localization.Farsi : storeLanguage === "ar" ? AG_GRID_LOCALE_EG : undefined,
    rowSelection: props.rowSelection === "single" ? "single" : "multiple",
    enableRangeSelection: !isMobileGrid,
    suppressCopyRowsToClipboard: true,
    enableCharts: true,
    rowDragManaged: true,
    enableRtl: storeLanguage !== "en",
    rowDragMultiRow: true,
    animateRows: true,
    groupSelectsChildren: true,
    groupSelectsFiltered: true,
    enableCellTextSelection: true,
    enableCellChangeFlash: true,
    groupIncludeTotalFooter: props.totalFooter,
    popupParent: document.querySelector("body"),
    allowContextMenuWithControlKey: true,
    // Default Column Definition
    defaultColDef: {
      minWidth: isMobileGrid ? 120 : 10,
      editable: false,
      sortable: true,
      resizable: true,
      filter: "agTextColumnFilter",
      floatingFilter: false,
    },

    // Sidebar Configuration
    sideBar:
      isMobileGrid || props.sideBar === "disable"
        ? ""
        : {
            toolPanels: ["columns", "filters"],
            position: "left",
            defaultToolPanel: "none",
          },

    // Status Bar Configuration
    statusBar:
      isMobileGrid || props.statusBar === false
        ? undefined
        : {
            statusPanels: [
              {
                statusPanel: LocalizedGridStatus,
                statusPanelParams: { kind: "totalFiltered" },
                align: "left",
              },
              { statusPanel: LocalizedGridStatus, statusPanelParams: { kind: "total" }, align: "center" },
              { statusPanel: LocalizedGridStatus, statusPanelParams: { kind: "filtered" } },
              { statusPanel: LocalizedGridStatus, statusPanelParams: { kind: "selected" } },
              { statusPanel: LocalizedGridStatus, statusPanelParams: { kind: "aggregation" } },
            ],
          },

    // Group Panel Configuration
    rowGroupPanelShow:
      groupingOpen ? "always" : "never",
    rowHeight: isMobileGrid ? 52 : undefined,
    headerHeight: isMobileGrid ? 44 : undefined,
    floatingFiltersHeight: isMobileGrid ? 0 : undefined,
    suppressRowHoverHighlight: isMobileGrid,
    suppressDragLeaveHidesColumns: true,

    // Aggregation Functions
    aggFuncs: {
      زیرگروه: (params) => {
        const uniqueValues = new Set(params.values);
        return Array.from(uniqueValues).join(", ");
      },
    },

    // Event Handlers — read grid id from propsRef so handler identity stays stable
    // and parent re-renders do not recreate defaultColDef / wipe column state.
    onColumnMoved: (params) => {
      if (preferenceRef.current.enabled) return;
      if (propsRef.current.isMobileGrid) {
        return;
      }
      persistColumnState(propsRef.current.id, params);
    },
    onColumnResized: (params) => {
      if (preferenceRef.current.enabled) return;
      if (propsRef.current.isMobileGrid || params?.finished === false) {
        return;
      }
      persistColumnState(propsRef.current.id, params);
    },
    onSortChanged: (params) => {
      if (preferenceRef.current.enabled) return;
      persistColumnState(propsRef.current.id, params);
    },
    onFilterChanged: (params) => {
      if (preferenceRef.current.enabled) return;
      const gridId = propsRef.current.id;
      if (!gridId) {
        return;
      }
      localStorage.setItem(
        `filtering/${gridId}`,
        JSON.stringify(params?.api?.getFilterModel()),
      );
    },
  }), [storeLanguage, props.rowSelection, props.totalFooter, props.sideBar, props.statusBar, isMobileGrid, groupingOpen]);

  const restorePersistedGridState = useCallback((params) => {
    const {
      id: gridId,
      skipPersistedColumnState,
      gridRef,
      setGridApi,
    } = propsRef.current;
    const colState = preferenceRef.current.enabled ? null : localStorage.getItem(`${gridId}`);
    const columnStateApi = getColumnStateApi(params);
    if (
      colState &&
      !skipPersistedColumnState &&
      !propsRef.current.isMobileGrid
    ) {
      try {
        columnStateApi?.applyColumnState?.({
          state: JSON.parse(colState),
          applyOrder: true,
        });
      } catch {
        // ignore invalid persisted state
      }
    }
    const filterModel = preferenceRef.current.enabled ? null : localStorage.getItem(`filtering/${gridId}`);
    if (filterModel) {
      try {
        params?.api?.setFilterModel(JSON.parse(filterModel));
      } catch {
        // ignore invalid persisted filters
      }
    }

    params?.api?.hideOverlay();
    if (gridRef) {
      gridRef(gridApiRef);
    }
    if (setGridApi) {
      setGridApi(params.api);
    }
  }, []);

  const getRowIdStable = useCallback((params) => {
    const getRowId = propsRef.current.getRowId;
    if (typeof getRowId === "function") {
      return getRowId(params);
    }
    return undefined;
  }, []);

  const hasGetRowId = typeof props.getRowId === "function";

  const defaultGridOptions = useMemo(() => {
    const sharedOptions = getSharedGridOptions();

    const masterOptions = {
      ...sharedOptions,
      // Master-specific configurations
      masterDetail: props.masterDetail,
      treeData: props.treeData,
      pinnedBottomRowData: props.pinnedBottomRowData,
      columnDefs: normalizedColumns,
      getRowId: hasGetRowId ? getRowIdStable : undefined,
      rowClassRules: props.rowClassRules,
      getDataPath: (data) => data.path,
      onGridReady: restorePersistedGridState,
    };

    if (props.masterDetail) {
      masterOptions.detailCellRendererParams = {
        detailGridOptions: {
          ...sharedOptions,
          columnDefs: props.detailColumns?.map(localizeColumn),
          onGridReady: (params) => {
            const {
              id: gridId,
              skipPersistedColumnState,
            } = propsRef.current;
            const colState = preferenceRef.current.enabled ? null : localStorage.getItem(`${gridId}`);
            const columnStateApi = getColumnStateApi(params);
            if (colState && !skipPersistedColumnState) {
              try {
                columnStateApi?.applyColumnState?.({
                  state: JSON.parse(colState),
                  applyOrder: true,
                });
              } catch {
                // ignore invalid persisted state
              }
            }
            setTimeout(() => {
              params.api.sizeColumnsToFit();
            }, 100);
          },
        },
        getDetailRowData: (params) => {
          params.successCallback(params.data.sketches);
        },
      };
    }

    return masterOptions;
  }, [getSharedGridOptions, props.masterDetail, props.treeData, normalizedColumns, getRowIdStable, restorePersistedGridState, hasGetRowId, props.rowClassRules, props.detailColumns, props.pinnedBottomRowData]);

  const columnDefaults = useMemo(function () {
    const defaults = props.gridOptions?.defaultColDef || defaultGridOptions.defaultColDef;
    return {
      ...(preference.enabled ? initialColumnDefaults(defaults) : defaults),
      ...(isMobileGrid && props.containerLayout ? { wrapHeaderText: true, autoHeaderHeight: true } : {}),
    };
  }, [props.gridOptions?.defaultColDef, defaultGridOptions.defaultColDef, preference.enabled, isMobileGrid, props.containerLayout]);
  const requestedStatusBar = props.gridOptions && Object.prototype.hasOwnProperty.call(props.gridOptions, "statusBar")
    ? props.gridOptions.statusBar : defaultGridOptions.statusBar;
  const statusBar = useMemo(function () { return localizeStatusBar(requestedStatusBar); }, [requestedStatusBar]);

  const filter = (text) => {
    setSearchText(text);
    const normalizedText = normalizeSearchValue(text);
    if (normalizedText.length === 0) {
      setFilteredData(dataSource);
    } else {
      const criteria = normalizedText.split(/\s+/);
      const result = dataSource.filter((item) => {
        const searchValue = props.getSearchValue
          ? props.getSearchValue(item)
          : item?.[props.filterBy];
        const normalizedSearchValue = normalizeSearchValue(searchValue);
        return criteria.every((criterion) => {
          return normalizedSearchValue.includes(criterion);
        });
      });
      setFilteredData(result);
    }
  };

  const clearSearch = () => {
    filter("");
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const renderActionButton = (tooltipTitle, onClick, icon) => (
    <Tooltip title={tooltipTitle}>
      <span className="ag-grid-toolbar-btn-trigger">
        <button
          type="button"
          onClick={onClick}
          className="btn btn-icon btn-clean btn-light-primary btn-circle btn-hover-bg-white ag-grid-toolbar-btn"
        >
          {icon}
        </button>
      </span>
    </Tooltip>
  );

  const handleRefresh = () => {
    if (props.onRefresh) {
      if (
        !props.hideOverlay &&
        !hasExplicitLoading(props.loading) &&
        !awaitingAutoLoadRef.current
      ) {
        awaitingAutoLoadRef.current = true;
        setIsLoading(true);
      }
      props.onRefresh();
      return;
    }

    if (isFullscreen) {
      writeFullscreenRestore(props.id);
    }

    window.location.reload();
  };

  const renderRefreshButton = () =>
    renderActionButton(
      t("بروز رسانی"),
      handleRefresh,
      <span className="svg-icon svg-icon-primary svg-icon-2x">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="24px"
          height="24px"
          viewBox="0 0 24 24"
          version="1.1"
        >
          <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
            <rect x={0} y={0} width={24} height={24} />
            <path
              d="M8.43296491,7.17429118 L9.40782327,7.85689436 C9.49616631,7.91875282 9.56214077,8.00751728 9.5959027,8.10994332 C9.68235021,8.37220548 9.53982427,8.65489052 9.27756211,8.74133803 L5.89079566,9.85769242 C5.84469033,9.87288977 5.79661753,9.8812917 5.74809064,9.88263369 C5.4720538,9.8902674 5.24209339,9.67268366 5.23445968,9.39664682 L5.13610134,5.83998177 C5.13313425,5.73269078 5.16477113,5.62729274 5.22633424,5.53937151 C5.384723,5.31316892 5.69649589,5.25819495 5.92269848,5.4165837 L6.72910242,5.98123382 C8.16546398,4.72182424 10.0239806,4 12,4 C16.418278,4 20,7.581722 20,12 C20,16.418278 16.418278,20 12,20 C7.581722,20 4,16.418278 4,12 L6,12 C6,15.3137085 8.6862915,18 12,18 C15.3137085,18 18,15.3137085 18,12 C18,8.6862915 15.3137085,6 12,6 C10.6885336,6 9.44767246,6.42282109 8.43296491,7.17429118 Z"
              fill="#000000"
              fillRule="nonzero"
            />
          </g>
        </svg>
      </span>,
    );

  const renderFullscreenButton = () => {
    if (props.disableFullscreen) {
      return null;
    }

    return renderActionButton(
      isFullscreen ? t("خروج از تمام صفحه") : t("نمایش تمام صفحه"),
      toggleFullscreen,
      isFullscreen ? (
        <FullscreenExitOutlined className="ag-grid-toolbar-btn-icon" />
      ) : (
        <FullscreenOutlined className="ag-grid-toolbar-btn-icon" />
      ),
    );
  };

  const renderToolbar = () => {
    const showSearch = !props.disableSearch;

    if (!showSearch) {
      return null;
    }

    const showFullscreen = !props.disableFullscreen;

    return (
      <div
        className="ag-grid-toolbar ag-grid-search-bar mb-5"
        data-mobile-grid={isMobileGrid || undefined}
      >
        <div className="ag-grid-search-field d-flex align-items-center">
          <label>{t("جستجو")}</label>
          <div className="ag-grid-search-input">
            <input
              type="text"
              value={searchText}
              onChange={(e) => filter(e?.target?.value ?? "")}
              className="form-control"
              placeholder={t("جستجو کنید ...")}
            />
            {searchText ? (
              <button
                type="button"
                className="ag-grid-search-clear"
                onClick={clearSearch}
                aria-label={t("پاک کردن جستجو")}
                title={t("پاک کردن")}
              >
                <CloseCircleFilled />
              </button>
            ) : null}
          </div>
          <div className="ag-grid-action-buttons">
            <PlatformIconButton
              label={groupingOpen ? t("عملیات ردیف‌ها") : t("گروه‌بندی ستون‌ها")}
              icon="group"
              aria-pressed={groupingOpen}
              onClick={() => setGrouping(!groupingOpen)}
            />
            {renderRefreshButton()}
            {showFullscreen && renderFullscreenButton()}
          </div>
        </div>
      </div>
    );
  };

  const gridBodyStyle = isFullscreen || props.containerLayout
    ? { position: "relative" }
    : {
        height: isMobileGrid
          ? "calc(100dvh - 220px)"
          : "calc(100vh - 170px)",
        position: "relative",
      };

  const gridContent = (
    <div
      className="ag-grid-with-actions"
      onPointerDownCapture={rowActions.onPointerDownCapture}
      onKeyDownCapture={rowActions.onKeyDownCapture}
    >
      {renderToolbar()}
      {rowActions.hasOperations && !groupingOpen && !inlineOperations ? (
        <GridRowActions
          {...rowActions}
          hideEmptyHint={isMobileGrid && !props.data?.length}
          onRun={rowActions.run}
          onClear={rowActions.clear}
        />
      ) : null}
      {isMobileGrid && normalizedColumns?.length > 2 && (!props.compactColumns || props.showAllMobileColumns) ? (
        <div className="ag-grid-mobile-hint" role="note">{t("برای مشاهده ستون‌های بیشتر، جدول را به طرفین بکشید.")} </div>
      ) : null}
      <div
        id="gridContainer"
        className={`ag-theme-quartz w-100 ag-grid-container ${
          isMobileGrid ? "ag-grid-container--mobile " : ""
        }${
          isFullscreen ? "ag-grid-container--fullscreen" : "100vh"
        }`}
        style={gridBodyStyle}
      >
        {!preference.hydrating ? (
          <AgGridReact
            key={storeLanguage + preference.key}
            treeData={props.treeData}
            {...defaultGridOptions}
            rowData={filteredData}
            {...props.gridOptions}
            {...inlineExportOptions}
            rowHeight={isMobileGrid && props.containerLayout ? 52 : props.gridOptions?.rowHeight ?? defaultGridOptions.rowHeight}
            quickFilterText={normalizeSearchValue(props.gridOptions?.quickFilterText)}
            statusBar={statusBar}
            {...Object.fromEntries(Object.entries(rowActions.events).map(([name, handler]) => [name, (event) => {
              handler(event);
              defaultGridOptions[name]?.(event);
              props.gridOptions?.[name]?.(event);
            }]))}
            rowGroupPanelShow={groupingOpen ? "always" : "never"}
            defaultColDef={columnDefaults}
            initialState={preference.enabled ? compactState : props.gridOptions?.initialState}
            includeHiddenColumnsInQuickFilter={props.compactColumns ? true : props.gridOptions?.includeHiddenColumnsInQuickFilter}
            onGridReady={function (event) {
              defaultGridOptions.onGridReady?.(event);
              props.gridOptions?.onGridReady?.(event);
              applyCompactColumns(event.api);
              if (preference.enabled) {
                for (const name of ["columnMoved", "columnResized", "columnVisible", "columnPinned",
                  "sortChanged", "filterChanged", "columnRowGroupChanged", "columnPivotChanged",
                  "columnPivotModeChanged", "columnValueChanged", "toolPanelVisibleChanged"]) {
                  event.api.addEventListener(name, function (change) { preferenceRef.current.capture(change); });
                }
              }
            }}
            onStateUpdated={function (event) {
              preference.schedule(event);
              props.gridOptions?.onStateUpdated?.(event);
            }}
            getMainMenuItems={function (params) {
              const items = props.gridOptions?.getMainMenuItems?.(params) || params.defaultItems;
              if (!preference.enabled) return items;
              return items.map(function (item) {
                return item === "resetColumns" ? {
                  name: t("بازنشانی تنظیمات جدول"),
                  disabled: !preference.canReset,
                  action: function () { void preference.reset(); },
                } : item;
              });
            }}
            getContextMenuItems={getContextMenuItems}
            ref={gridApiRef}
          />
        ) : null}
        {isLoading || preference.hydrating || preference.resetting ? <LoadingRing overlay /> : null}
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <div className="ag-grid-fullscreen-wrapper">{gridContent}</div>
    );
  }

  return gridContent;
};

export default AgGrid;
